import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { RoofSegmentSizeAndSunshineStats } from '../../../../models/maps/building-insights';
import { GoogleMapsService } from '../../../../../services/google-maps.service';
import { inOutAnimation } from '../../../../animations';
import { CalculatorColorConfig, defaultCalculatorColorConfig } from '../simple-calculator/calculator-color-config';

@Component({
  selector: 'psc-simple-draw-calculator-map',
  templateUrl: './simple-draw-calculator-map.component.html',
  styleUrls: ['./simple-draw-calculator-map.component.scss'],
  animations: [
    inOutAnimation({}),
  ],
})
export class SimpleDrawCalculatorMapComponent {

  @Input() orientation: number;
  @Input() countries: string[] = ['HR', 'BA'];

  @Input() set dataInputDone(value: boolean) {
    this._dataInputDone = value;
    const newGestureMode = value ? 'none' : 'auto';
    this.mapOptions = {
      ...this.mapOptions,
      gestureHandling: newGestureMode,
    };
    this.updateStaticMapImageUrl();
  }

  @Output() locationSet: EventEmitter<google.maps.LatLngLiteral> = new EventEmitter<google.maps.LatLngLiteral>();
  @Output() roofSegmentSelected: EventEmitter<RoofSegmentSizeAndSunshineStats> = new EventEmitter<RoofSegmentSizeAndSunshineStats>();
  /** Emitted when the user clears the roof polygon (×); form should reset until they draw again. */
  @Output() drawingCleared = new EventEmitter<void>();
  @Output() staticMapImageUrlChange = new EventEmitter<string | undefined>();

  /** Lets the host set map size via CSS (responsive height/width). */
  readonly googleMapCssSize: null = null;

  _dataInputDone: boolean;
  center: google.maps.LatLngLiteral = { lat: 43.3438, lng: 17.8078 };
  markerPosition?: google.maps.LatLngLiteral;
  mapZoom = 15;
  mapOptions: google.maps.MapOptions = {
    tilt: 0,
    rotateControl: false,
    mapTypeId: google.maps.MapTypeId.HYBRID,
  };

  addressValue: string;
  locationNotSelectedError: boolean;
  staticMapImageUrl?: string;

  private readonly staticMapFrameSize = 260;
  private readonly staticMapZoom = 20;

  private mapInstance: google.maps.Map;
  private polygonClickListener: google.maps.MapsEventListener | null = null;
  private drawnPolygon: google.maps.Polygon | null = null;
  /** Numbered dots marking each corner while the roof is still being drawn. */
  private vertexDots: google.maps.Marker[] = [];
  private mapsApiKey: string | null | undefined;
  private staticMapValidationToken = 0;
  drawnAreaM2: number | null = null;
  isPolygonFinished = false;

  /** Touch devices get larger corner dots and a more forgiving close-the-polygon radius. */
  private readonly isTouch = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
  /** Zoom applied once a location is known, so corners can be placed precisely. */
  private readonly drawingZoom = 20;
  private readonly minimumPointsForRoof = 3;

  constructor(
    private googleMapsService: GoogleMapsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  mapInitialized(map: google.maps.Map) {
    this.mapInstance = map;
    this.startPolygonDrawing();
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          if (position) {
            const userLocation = { lat: position.coords.latitude, lng: position.coords.longitude };
            this.setLocationAndFillAddress(userLocation);
            this.center = userLocation;
            // Zoom in so roof corners can be placed accurately right away.
            this.mapZoom = Math.max(this.mapZoom, this.drawingZoom);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  setLocation(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this._dataInputDone) {
      return;
    }
    this.locationNotSelectedError = false;
    this.markerPosition = location;
    this.updateStaticMapImageUrl();
    this.locationSet.emit(this.markerPosition);
  }

  setLocationAndFillAddress(location: google.maps.LatLngLiteral | undefined) {
    if (!location || this._dataInputDone) {
      return;
    }

    this.googleMapsService.getAddressFromLatLngPromise(location).subscribe(
      (res) => {
        if (res?.length > 0) {
          this.addressValue = res[0].formatted_address;
        }
      });
    this.setLocation(location);
  }

  /**
   * Google removed the Drawing Library (DrawingManager) from the Maps JavaScript API
   * (deprecated Aug 2025, unavailable as of May 2026), so roof corners are placed by
   * hand via plain map clicks instead of the old drawingMode/polygoncomplete flow.
   */
  private startPolygonDrawing() {
    if (!this.mapInstance) return;

    this.polygonClickListener?.remove();
    this.polygonClickListener = this.mapInstance.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (!event.latLng || this.isPolygonFinished) return;
      // Clicking (or tapping near) the first corner closes the roof, like other map drawing tools.
      if (this.isNearFirstVertex(event.latLng)) {
        this.finishDrawing();
        return;
      }
      this.addDrawingPoint(event.latLng);
    });
  }

  private addDrawingPoint(latLng: google.maps.LatLng) {
    const primaryColor = this.colorConfig.primaryColor || '#1B4F8A';
    if (!this.drawnPolygon) {
      this.drawnPolygon = new google.maps.Polygon({
        map: this.mapInstance,
        paths: [latLng],
        strokeColor: primaryColor,
        fillColor: primaryColor,
        fillOpacity: 0.35,
        strokeWeight: 2,
        clickable: false,
      });
    } else {
      this.drawnPolygon.getPath().push(latLng);
    }

    this.addVertexDot(latLng, primaryColor);
    this.updateLiveArea();
    this.refreshFirstVertexStyle();
    this.changeDetectorRef.detectChanges();
  }

  /** Removes the last placed corner so a misclick doesn't force a full redraw. */
  undoLastPoint() {
    const path = this.drawnPolygon?.getPath();
    if (this.isPolygonFinished || !path || path.getLength() === 0) return;

    path.removeAt(path.getLength() - 1);
    this.vertexDots.pop()?.setMap(null);

    if (path.getLength() === 0) {
      this.drawnPolygon?.setMap(null);
      this.drawnPolygon = null;
    }

    this.updateLiveArea();
    this.refreshFirstVertexStyle();
    this.changeDetectorRef.detectChanges();
  }

  private addVertexDot(latLng: google.maps.LatLng, primaryColor: string) {
    const dot = new google.maps.Marker({
      position: latLng,
      map: this.mapInstance,
      clickable: false,
      icon: this.vertexIcon(primaryColor, false),
      label: {
        text: String(this.vertexDots.length + 1),
        color: primaryColor,
        fontSize: this.isTouch ? '11px' : '10px',
        fontWeight: 'bold',
      },
    });
    this.vertexDots.push(dot);
  }

  private vertexIcon(primaryColor: string, isClosingTarget: boolean): google.maps.Symbol {
    return {
      path: google.maps.SymbolPath.CIRCLE,
      scale: this.isTouch ? 11 : 9,
      fillColor: '#FFFFFF',
      fillOpacity: 1,
      strokeColor: primaryColor,
      // The first corner gets a thicker ring once it can close the roof, hinting it's tappable.
      strokeWeight: isClosingTarget ? 4 : 2,
      labelOrigin: new google.maps.Point(0, 0),
    };
  }

  /** Highlights the first corner while it is a valid target for closing the polygon. */
  private refreshFirstVertexStyle() {
    const firstDot = this.vertexDots[0];
    if (!firstDot) return;
    const primaryColor = this.colorConfig.primaryColor || '#1B4F8A';
    firstDot.setIcon(this.vertexIcon(primaryColor, this.canFinishDrawing));
  }

  /** Keeps the on-map area readout in sync while the user is still placing corners. */
  private updateLiveArea() {
    const path = this.drawnPolygon?.getPath();
    this.drawnAreaM2 = path && path.getLength() >= this.minimumPointsForRoof
      ? google.maps.geometry.spherical.computeArea(path)
      : null;
  }

  /** Approximate metres covered by one screen pixel, so the close radius feels the same at any zoom. */
  private metersPerPixel(latitude: number): number {
    const zoom = this.mapInstance?.getZoom() ?? this.mapZoom;
    return 156543.03392 * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom);
  }

  private isNearFirstVertex(latLng: google.maps.LatLng): boolean {
    if (!this.canFinishDrawing) return false;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const firstVertex = this.drawnPolygon!.getPath().getAt(0);
    const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(firstVertex, latLng);
    const radiusPx = this.isTouch ? 28 : 18;
    return distanceMeters <= radiusPx * this.metersPerPixel(latLng.lat());
  }

  private clearVertexDots() {
    this.vertexDots.forEach((dot) => dot.setMap(null));
    this.vertexDots = [];
  }

  get pointsPlaced(): number {
    return this.drawnPolygon?.getPath().getLength() ?? 0;
  }

  get canUndoLastPoint(): boolean {
    return !this.isPolygonFinished && this.pointsPlaced > 0;
  }

  get canFinishDrawing(): boolean {
    return !this.isPolygonFinished && this.pointsPlaced >= this.minimumPointsForRoof;
  }

  /** Drives the on-map instruction banner; key is read under scope `home.calculator.map`. */
  get drawingHintKey(): string {
    if (this.canFinishDrawing) return 'draw_roof.hint_close';
    if (this.pointsPlaced > 0) return 'draw_roof.hint_more_points';
    return 'draw_roof.hint_start';
  }

  finishDrawing() {
    if (!this.canFinishDrawing || !this.drawnPolygon) return;

    this.polygonClickListener?.remove();
    this.polygonClickListener = null;
    this.isPolygonFinished = true;
    this.clearVertexDots();

    const polygon = this.drawnPolygon;
    polygon.setOptions({ editable: true, draggable: true, clickable: true });

    this.drawnAreaM2 = google.maps.geometry.spherical.computeArea(polygon.getPath());

    const centroid = this.computeCentroid(polygon.getPath().getArray());
    this.setLocationAndFillAddress(centroid);

    const path = polygon.getPath();
    google.maps.event.addListener(path, 'set_at', () => this.recalculateArea());
    google.maps.event.addListener(path, 'insert_at', () => this.recalculateArea());
    google.maps.event.addListener(path, 'remove_at', () => this.recalculateArea());
    google.maps.event.addListener(polygon, 'dragend', () => this.recalculateArea());

    this.emitDrawnRoofSegment();
    this.changeDetectorRef.detectChanges();
  }

  private recalculateArea() {
    if (!this.drawnPolygon) return;
    this.drawnAreaM2 = google.maps.geometry.spherical.computeArea(this.drawnPolygon.getPath());
    this.setLocation(this.computeCentroid(this.drawnPolygon.getPath().getArray()));
    this.emitDrawnRoofSegment();
    this.changeDetectorRef.detectChanges();
  }

  private emitDrawnRoofSegment() {
    if (!this.drawnPolygon || this.drawnAreaM2 === null) return;
    const centroid = this.computeCentroid(this.drawnPolygon.getPath().getArray());
    const segment: RoofSegmentSizeAndSunshineStats = {
      stats: {
        areaMeters2: Math.round(this.drawnAreaM2),
        sunshineQuantiles: [],
        groundAreaMeters2: Math.round(this.drawnAreaM2),
      },
      center: { latitude: centroid.lat, longitude: centroid.lng },
      boundingBox: { sw: { latitude: centroid.lat, longitude: centroid.lng }, ne: { latitude: centroid.lat, longitude: centroid.lng } },
      pitchDegrees: 0,
      azimuthDegrees: 180,
      planeHeightAtCenterMeters: 0,
    };
    this.roofSegmentSelected.emit(segment);
  }

  resetDrawing() {
    if (this.drawnPolygon) {
      this.drawnPolygon.setMap(null);
      this.drawnPolygon = null;
    }
    this.clearVertexDots();
    this.isPolygonFinished = false;
    this.drawnAreaM2 = null;
    this.markerPosition = undefined;
    this.updateStaticMapImageUrl();
    this.startPolygonDrawing();
    this.drawingCleared.emit();
    this.changeDetectorRef.detectChanges();
  }

  private computeCentroid(points: google.maps.LatLng[]): google.maps.LatLngLiteral {
    let lat = 0;
    let lng = 0;
    for (const point of points) {
      lat += point.lat();
      lng += point.lng();
    }
    return { lat: lat / points.length, lng: lng / points.length };
  }

  @Input() colorConfig: CalculatorColorConfig = defaultCalculatorColorConfig;

  setAddress(location: google.maps.places.PlaceResult) {
    this.setLocation(location.geometry?.location?.toJSON());
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.center = this.markerPosition!;
    this.mapZoom = 21;
    this.changeDetectorRef.detectChanges();
  }

  private updateStaticMapImageUrl() {
    if (!this.markerPosition) {
      this.clearStaticMapImageUrl();
      return;
    }

    const apiKey = this.getGoogleMapsApiKey();
    if (!apiKey) {
      this.clearStaticMapImageUrl();
      return;
    }

    const { lat, lng } = this.markerPosition;
    const params = new URLSearchParams({
      center: `${ lat.toFixed(6) },${ lng.toFixed(6) }`,
      zoom: String(Math.max(this.mapZoom, this.staticMapZoom)),
      size: `${ this.staticMapFrameSize }x${ this.staticMapFrameSize }`,
      maptype: 'satellite',
      markers: `color:red|${ lat },${ lng }`,
      key: apiKey,
    });

    this.validateAndSetStaticMapImageUrl(`https://maps.googleapis.com/maps/api/staticmap?${ params.toString() }`);
  }

  private clearStaticMapImageUrl() {
    this.staticMapValidationToken++;
    this.staticMapImageUrl = undefined;
    this.staticMapImageUrlChange.emit(this.staticMapImageUrl);
  }

  private validateAndSetStaticMapImageUrl(staticMapImageUrl: string) {
    const validationToken = ++this.staticMapValidationToken;
    this.staticMapImageUrl = undefined;
    this.staticMapImageUrlChange.emit(this.staticMapImageUrl);

    const image = new Image();
    image.onload = () => {
      if (validationToken !== this.staticMapValidationToken) {
        return;
      }

      this.staticMapImageUrl = staticMapImageUrl;
      this.staticMapImageUrlChange.emit(this.staticMapImageUrl);
      this.changeDetectorRef.detectChanges();
    };

    image.onerror = () => {
      if (validationToken !== this.staticMapValidationToken) {
        return;
      }

      this.staticMapImageUrl = undefined;
      this.staticMapImageUrlChange.emit(this.staticMapImageUrl);
      this.changeDetectorRef.detectChanges();
    };

    image.src = staticMapImageUrl;
  }

  private getGoogleMapsApiKey(): string | null {
    if (this.mapsApiKey !== undefined) {
      return this.mapsApiKey;
    }

    try {
      const googleMapsScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement | null;
      if (!googleMapsScript?.src) {
        this.mapsApiKey = null;
        return this.mapsApiKey;
      }

      const scriptUrl = new URL(googleMapsScript.src);
      this.mapsApiKey = scriptUrl.searchParams.get('key');
      return this.mapsApiKey;
    } catch {
      this.mapsApiKey = null;
      return this.mapsApiKey;
    }
  }
}
